import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AdminAPI,
  useAddNumberRequestDelivery,
  useAdminNumberRequest,
  useProvisionNumberRequest,
  useRemoveNumberRequestDelivery,
  useTransitionNumberRequest,
  useUpdateAdminNumberRequest,
  type AdminNumberRequest,
  type AdminTrunkAttachment,
} from "@/features/admin";
import {
  NUMBER_REQUEST_HANDSETS,
  NUMBER_REQUEST_TERMINAL_STATES,
  NUMBER_REQUEST_TRANSITIONS,
  handsetsOrdered,
  hardwareOrderTotal,
  type NumberRequestDocument,
  type NumberRequestState,
} from "@/features/number-requests/types";
import { openBlobInTab } from "@/features/utils/blob";
import { useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import { NumberRequestStateBadge } from "@/views/backoffice/connectors/components/number-requests/state-badge";
import {
  EnvelopeSimpleIcon,
  EyeIcon,
  PackageIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminPageLayout } from "../layout";
import { WaitingOnBadge } from "./waiting-on";

/** How the company behind a guided request was established (`company_check`). */
const COMPANY_CHECK_LABELS: Record<string, string> = {
  verified: "SIREN vérifié dans l'annuaire",
  unverified: "SIREN non vérifié automatiquement — à contrôler",
  foreign: "Société non française — voir son document d'enregistrement",
  "": "",
};

/**
 * One tenant's number request, worked from the platform back-office.
 *
 * Deliberately not the tenant's page in disguise: the client fills a form in
 * blocks and uploads papers, we read those papers and move the file along. So
 * what is writable here is exactly the two things the tenant cannot know or do
 * — the number the carrier gave us, and the next step of the state machine —
 * and everything else is shown read-only, including the notes, which are the
 * client's own words about their situation.
 *
 * The client is kept informed from here too ({@link FollowUpCard}): each
 * tracking link added e-mails them, and so does validating the file.
 *
 * The last step of the process has a button of its own ({@link NumberCard}):
 * validating a file is not a state change, it is the moment the client gets a
 * working line, so the number, the channel and the state move together rather
 * than as three things to remember in order.
 */

const formatDateTime = (at: number) =>
  at ? new Date(at).toLocaleString() : "—";

/** One line of the read-only summary. */
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </p>
      <div className="text-sm">{children || "—"}</div>
    </div>
  );
}

/**
 * A supporting document, read-only.
 *
 * The download route is authenticated, so the file cannot simply be linked to:
 * it comes back as a blob and is handed to the browser as an object URL, the
 * same way the tenant page does it.
 */
function DocumentRow({
  clientId,
  requestId,
  document,
}: {
  clientId: string;
  requestId: string;
  document: NumberRequestDocument;
}) {
  const [isOpening, setIsOpening] = useState(false);

  const handleOpen = async () => {
    setIsOpening(true);
    try {
      const blob = await AdminAPI.downloadNumberRequestDocument(
        clientId,
        requestId,
        document.type,
      );
      openBlobInTab(blob);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Impossible d'ouvrir la pièce",
      );
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b py-3 last:border-0">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{document.label}</p>
        <p className="text-muted-foreground truncate text-xs">
          {document.provided
            ? [
                document.filename,
                document.size ? formatFileSize(document.size) : "",
                document.uploaded_at
                  ? formatDateTime(document.uploaded_at)
                  : "",
              ]
                .filter(Boolean)
                .join(" · ")
            : "Pas encore fournie"}
        </p>
      </div>

      {document.provided && document.file_key && (
        <Button
          variant="outline"
          size="sm"
          disabled={isOpening}
          onClick={handleOpen}
        >
          <EyeIcon className="h-4 w-4" />
          Voir
        </Button>
      )}
    </div>
  );
}

/**
 * The next steps the state machine allows, plus the comment that goes with the
 * move. The comment is the only place a refusal can be explained, and the
 * client reads it on their own page — so it is offered on every transition, not
 * just on `rejected`.
 *
 * `provisioned` is deliberately missing from the buttons: reaching it is not a
 * state change on its own, it is the moment the client's line starts working,
 * and it goes through {@link NumberCard} so the number and the channel are
 * created with it. A bare transition here would leave a file marked done with
 * nothing behind it, which is the failure this page exists to prevent.
 */
function TransitionCard({ request }: { request: AdminNumberRequest }) {
  const { t } = useTranslation("numberRequests");
  const [comment, setComment] = useState("");

  const transition = useTransitionNumberRequest(request.client_id, request.id);
  const allowed = (NUMBER_REQUEST_TRANSITIONS[request.state] || []).filter(
    (state) => state !== "provisioned",
  );

  const move = async (state: NumberRequestState) => {
    try {
      await transition.mutateAsync({
        state,
        comment: comment.trim() || undefined,
      });
      setComment("");
      toast.success(
        `Demande passée à « ${t(`numberRequests.states.${state}`)} »`,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Transition refusée",
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Faire avancer</CardTitle>
        <CardDescription>
          {allowed.length
            ? "Le commentaire est ajouté à l'historique, que le client voit sur sa propre page."
            : "Cette demande est à une étape finale : elle ne bouge plus."}
        </CardDescription>
      </CardHeader>

      {!!allowed.length && (
        <CardContent className="space-y-3">
          <Textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Commentaire (facultatif) — le motif d'un refus se met ici."
            rows={2}
          />
          <div className="flex flex-wrap gap-2">
            {allowed.map((state) => (
              <Button
                key={state}
                variant={
                  state === "rejected" || state === "cancelled"
                    ? "outline"
                    : "default"
                }
                size="sm"
                disabled={transition.isPending}
                onClick={() => move(state)}
              >
                {t(`numberRequests.states.${state}`)}
              </Button>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

/** What validating did at Twilio, in one line for the toast. */
const describeTrunk = (trunk?: AdminTrunkAttachment | null): string => {
  if (!trunk) {
    return "Aucun compte Twilio HeyUp configuré : le numéro n'a pas été rattaché à un trunk.";
  }
  const name = trunk.trunk_name || trunk.trunk_domain || trunk.trunk_sid;
  const region = trunk.previous_voice_region
    ? `région ${trunk.previous_voice_region} → ${trunk.voice_region}`
    : `région ${trunk.voice_region}`;
  return `${trunk.attached ? "Rattaché au" : "Déjà sur le"} trunk ${name}, ${region}.`;
};

/**
 * The number, and the button that turns the file into a working line.
 *
 * Two things happen here and they are deliberately not two cards. Saving the
 * number is an edit: on a `new_number` request it is empty until the carrier
 * hands us one, and we are the ones who learn it, so it can be recorded at any
 * point while the file is still moving. Validating is the end: it records the
 * number, creates the client's "Numéro HeyUp" channel with it and marks the
 * request provisioned, in one server-side action — because a number saved on a
 * request nobody connected answers nothing, and a request marked provisioned
 * with no channel behind it says the opposite of what is true.
 *
 * The validate button is offered from any live step, not only from `approved`.
 * The ladder in between describes a carrier file — papers collected, papers
 * reviewed, file lodged — and a number we hand over directly never had one, so
 * clicking through four steps to unlock the last would only write four entries
 * into the audit trail claiming we reviewed documents nobody uploaded. The
 * server records the one thing that happened instead; see
 * `sessions/services/number-requests.ts#markProvisioned`.
 */
function NumberCard({ request }: { request: AdminNumberRequest }) {
  const [value, setValue] = useState(request.phone_number || "");
  const [comment, setComment] = useState("");
  const update = useUpdateAdminNumberRequest(request.client_id, request.id);
  const provision = useProvisionNumberRequest(request.client_id, request.id);

  // Everything except the three closed states. A cancelled or rejected request
  // is a decision someone took, and reopening it is not the same act as
  // provisioning one.
  const canProvision = !NUMBER_REQUEST_TERMINAL_STATES.includes(request.state);
  // A SIP trunk ends without a number of ours: we open an access, no line is
  // created, so there is nothing to attach and nothing to require.
  const needsNumber = request.kind !== "sip_trunk";
  const number = value.trim();

  const save = async () => {
    try {
      await update.mutateAsync({ phone_number: number });
      toast.success("Numéro enregistré");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Enregistrement impossible",
      );
    }
  };

  const validate = async () => {
    try {
      const provisioned = await provision.mutateAsync({
        phone_number: number,
        comment: comment.trim() || undefined,
      });
      setComment("");
      toast.success(
        needsNumber
          ? `Dossier validé — ${number} est branché sur le canal du client.`
          : "Dossier validé.",
        needsNumber
          ? { description: describeTrunk(provisioned.trunk) }
          : undefined,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Validation impossible",
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Numéro attribué</CardTitle>
        <CardDescription>
          Ce que l'opérateur nous a donné. Le client le voit dès qu'il est
          saisi.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="+33…"
            className="max-w-xs"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={
              update.isPending || number === (request.phone_number || "")
            }
            onClick={save}
          >
            {update.isPending ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>

        {canProvision && (
          <div className="space-y-3 rounded-lg border p-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Valider le dossier</p>
              <p className="text-muted-foreground text-xs">
                {needsNumber
                  ? "Enregistre le numéro, crée le canal « Numéro HeyUp » du client avec ce numéro et le branche sur son agent (celui de sa ligne de démo, sinon son premier agent), retire la ligne de démo, rattache le numéro au trunk SIP Twilio et lui donne la région du trunk, passe la demande à « Provisionné » et envoie l'e-mail de mise en service. La ligne tourne sur notre compte opérateur : le client n'a aucun identifiant à saisir."
                  : "Aucun numéro n'est créé pour un accès SIP : la demande passe simplement à « Provisionné »."}{" "}
                Possible depuis n'importe quelle étape : un numéro que nous
                remettons directement n'a pas de dossier opérateur à instruire.
              </p>
            </div>

            <Textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Commentaire (facultatif) — ajouté à l'historique, que le client voit."
              rows={2}
            />

            <Button
              size="sm"
              disabled={provision.isPending || (needsNumber && !number)}
              onClick={validate}
            >
              {provision.isPending
                ? "Validation…"
                : "Valider et brancher le numéro"}
            </Button>

            {needsNumber && !number && (
              <p className="text-muted-foreground text-xs">
                Renseignez le numéro attribué pour pouvoir valider.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Who hears about this request, and the parcels on their way.
 *
 * The client is told two things by e-mail: a parcel left (each tracking link
 * added here) and the number is live (on validation). Both go to the address on
 * the request, or to the tenant's owners when there is none — which reaches
 * nobody for a client we set up ourselves before anyone joined, hence the field
 * sitting right above the button that sends.
 */
function FollowUpCard({ request }: { request: AdminNumberRequest }) {
  const [email, setEmail] = useState(request.notification_email || "");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [label, setLabel] = useState("");

  const update = useUpdateAdminNumberRequest(request.client_id, request.id);
  const add = useAddNumberRequestDelivery(request.client_id, request.id);
  const remove = useRemoveNumberRequestDelivery(request.client_id, request.id);

  const deliveries = request.deliveries?.items || [];
  const savedEmail = request.notification_email || "";
  const recipient = savedEmail || "les propriétaires du compte";

  const saveEmail = async () => {
    try {
      await update.mutateAsync({ notification_email: email.trim() });
      toast.success("Adresse de suivi enregistrée");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Enregistrement impossible",
      );
    }
  };

  const addDelivery = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const updated = await add.mutateAsync({
        tracking_url: trackingUrl.trim(),
        label: label.trim() || undefined,
      });
      setTrackingUrl("");
      setLabel("");
      const last = updated.deliveries?.items?.at(-1);
      if (last?.notified_to?.length) {
        toast.success(`Lien de suivi envoyé à ${last.notified_to.join(", ")}`);
      } else {
        toast.warning(
          "Livraison ajoutée, mais aucun e-mail n'est parti : pas d'adresse sur la demande et aucun propriétaire sur le compte.",
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Ajout impossible");
    }
  };

  const removeDelivery = async (deliveryId: string) => {
    try {
      await remove.mutateAsync(deliveryId);
      toast.success("Livraison retirée");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Suppression impossible",
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Suivi client</CardTitle>
        <CardDescription>
          Le client reçoit un e-mail à chaque lien de suivi ajouté, puis quand
          le numéro est actif.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <p className="text-sm font-medium">E-mail de notification</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="contact@client.fr"
              className="max-w-xs"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={update.isPending || email.trim() === savedEmail}
              onClick={saveEmail}
            >
              {update.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Vide : les e-mails partent aux propriétaires du compte, s'il y en a
            déjà.
            {!!request.activation_notified_at &&
              ` Mise en service annoncée le ${formatDateTime(request.activation_notified_at)}.`}
          </p>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium">Livraisons</p>

          {deliveries.length ? (
            <div className="rounded-lg border">
              {deliveries.map((delivery) => (
                <div
                  key={delivery.id}
                  className="flex items-start justify-between gap-3 border-b p-3 last:border-0"
                >
                  <div className="min-w-0 space-y-0.5">
                    <p className="flex items-center gap-1.5 text-sm font-medium">
                      <PackageIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {delivery.label || "Colis"}
                      </span>
                    </p>
                    <a
                      href={delivery.tracking_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-primary block truncate text-xs hover:underline"
                    >
                      {delivery.tracking_url}
                    </a>
                    <p className="text-muted-foreground flex items-center gap-1 text-xs">
                      <EnvelopeSimpleIcon className="h-3.5 w-3.5 shrink-0" />
                      {delivery.notified_at
                        ? `Envoyé à ${(delivery.notified_to || []).join(", ")} le ${formatDateTime(delivery.notified_at)}`
                        : "Aucun e-mail envoyé"}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={remove.isPending}
                    onClick={() => removeDelivery(delivery.id)}
                    title="Retirer cette livraison"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              Aucune livraison pour l'instant.
            </p>
          )}

          <form
            onSubmit={addDelivery}
            className="space-y-2 rounded-lg border p-3"
          >
            <Input
              type="url"
              value={trackingUrl}
              onChange={(event) => setTrackingUrl(event.target.value)}
              placeholder="https://www.laposte.fr/outils/suivre-vos-envois?code=…"
              required
            />
            <Input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Transporteur ou contenu (facultatif) — ex. Colissimo, 2 postes fixes"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-muted-foreground text-xs">
                Envoie le lien par e-mail à {recipient}.
              </p>
              <Button
                type="submit"
                size="sm"
                disabled={add.isPending || !trackingUrl.trim()}
              >
                <EnvelopeSimpleIcon className="h-4 w-4" />
                {add.isPending ? "Envoi…" : "Ajouter et notifier"}
              </Button>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminNumberRequestPage() {
  const { t } = useTranslation("numberRequests");
  const { clientId = "", requestId = "" } = useParams<{
    clientId: string;
    requestId: string;
  }>();

  const { data: request, isLoading } = useAdminNumberRequest(
    clientId,
    requestId,
  );

  const backTo = {
    label: "Retour aux demandes",
    path: `/admin/number-requests`,
  };

  if (isLoading) {
    return (
      <AdminPageLayout title="Demande de numéro" backTo={backTo}>
        <Skeleton className="h-64 w-full rounded-lg" />
      </AdminPageLayout>
    );
  }

  if (!request) {
    return (
      <AdminPageLayout title="Demande de numéro" backTo={backTo}>
        <p className="text-muted-foreground text-sm">
          Cette demande est introuvable. Elle a peut-être été supprimée.
        </p>
      </AdminPageLayout>
    );
  }

  const documents = request.documents?.items || [];
  const history = request.history?.items || [];
  const handsets = handsetsOrdered(request.hardware_order);

  return (
    <AdminPageLayout
      title={request.client_name || request.client_id}
      description={t(`numberRequests.kinds.${request.kind}`)}
      backTo={backTo}
      actions={
        <div className="flex items-center gap-2">
          <WaitingOnBadge waitingOn={request.waiting_on} />
          <NumberRequestStateBadge state={request.state} />
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <TransitionCard request={request} />
          <NumberCard request={request} />
          <FollowUpCard request={request} />

          <Card>
            <CardHeader>
              <CardTitle>Informations</CardTitle>
              <CardDescription>
                Ce que le client a renseigné. En lecture seule : c'est sa
                situation, pas la nôtre.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Tenant">
                <span className="font-mono text-xs">{request.client_id}</span>
              </Field>
              <Field label="Pays">{request.country}</Field>
              <Field label="Préfixe souhaité">{request.desired_prefix}</Field>
              {request.holder_type && (
                <Field label="Titulaire">
                  {request.holder_type === "individual" ? (
                    "Personne physique"
                  ) : (
                    <div className="space-y-0.5">
                      <p>{request.company_name || "—"}</p>
                      {request.company_siren && (
                        <p className="text-muted-foreground tabular-nums">
                          SIREN {request.company_siren}
                        </p>
                      )}
                      {request.company_address && (
                        <p className="text-muted-foreground">
                          {request.company_address}
                        </p>
                      )}
                      <p className="text-muted-foreground">
                        {COMPANY_CHECK_LABELS[request.company_check || ""]}
                      </p>
                    </div>
                  )}
                </Field>
              )}
              <Field label="Créée le">
                {formatDateTime(request.created_at || 0)}
              </Field>
              <Field label="Matériel">
                {handsets || request.hardware_order?.own_device ? (
                  <div className="space-y-0.5">
                    {NUMBER_REQUEST_HANDSETS.map((handset) => {
                      const quantity =
                        request.hardware_order?.quantities?.[handset] || 0;
                      if (!quantity) return null;
                      return (
                        <p key={handset}>
                          {quantity} ×{" "}
                          {t(
                            `numberRequests.details.hardwareOptions.${handset}.title`,
                          )}
                        </p>
                      );
                    })}
                    {request.hardware_order?.own_device && (
                      <p>Téléphone VoIP / SIP personnel</p>
                    )}
                    {!!handsets && (
                      <p className="text-muted-foreground">
                        Total : {hardwareOrderTotal(request.hardware_order)} €
                      </p>
                    )}
                  </div>
                ) : (
                  "Aucun"
                )}
              </Field>
              <Field label="Précisions du client">
                {request.notes ? (
                  <p className="whitespace-pre-wrap">{request.notes}</p>
                ) : null}
              </Field>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pièces justificatives</CardTitle>
              <CardDescription>
                {documents.length
                  ? `${request.documents_provided}/${request.documents_total} fournies — c'est ce qui est déposé chez l'opérateur.`
                  : "Cette demande n'en réclame aucune."}
              </CardDescription>
            </CardHeader>
            {!!documents.length && (
              <CardContent className="py-0">
                {documents.map((document) => (
                  <DocumentRow
                    key={document.type}
                    clientId={request.client_id}
                    requestId={request.id}
                    document={document}
                  />
                ))}
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Historique</CardTitle>
              <CardDescription>
                Chaque étape franchie, et par qui.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {history.length ? (
                [...history].reverse().map((entry, index) => (
                  <div
                    key={`${entry.at}-${index}`}
                    className="border-b pb-3 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {t(`numberRequests.states.${entry.state}`)}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {formatDateTime(entry.at)}
                      </span>
                    </div>
                    <p className="text-muted-foreground font-mono text-xs">
                      {entry.by}
                    </p>
                    {entry.comment && (
                      <p className="mt-1 text-sm whitespace-pre-wrap">
                        {entry.comment}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">Rien encore.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminPageLayout>
  );
}
